import { useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  selectAuthUser,
  selectContextUser,
} from "@/features/auth/store/authSelectors";
import {
  setConversations,
  setConversationsLoading,
  setMessagesLoading,
  setMessagesForConversation,
  setActiveConversationId,
  addMessage,
  markConversationRead,
  setChatError,
  setSidebarMeta,
  upsertConversation,
} from "../store/chatSlice";
import {
  selectChatConversations,
  selectActiveConversationId,
  selectMessagesForActiveConversation,
  selectActiveConversation,
} from "../store/chatSelectors";
import {
  fetchConversations,
  fetchMessages,
  sendMessageApi,
  markConversationReadApi,
  createOrGetDirectConversation,
} from "../services/chatService";
import { useChatSocket } from "./useChatSocket";
import { getAccessToken } from "@/lib/authSession";
import { ROLES } from "@/constants/roles";
import { pillarChairService } from "@/features/pillarChair/services/pillarChairService";

export function useChat() {
  const dispatch = useAppDispatch();
  const account = useAppSelector(selectAuthUser);
  const user = useAppSelector(selectContextUser);
  const conversations = useAppSelector(selectChatConversations);
  const activeId = useAppSelector(selectActiveConversationId);
  const messages = useAppSelector(selectMessagesForActiveConversation);
  const activeConversation = useAppSelector(selectActiveConversation);

  /** Same id used for optimistic sends — prefer auth.user.id, fall back to account.userId */
  const userId =
    account?.user?.id != null && String(account.user.id).trim() !== ""
      ? String(account.user.id).trim()
      : account?.userId != null && String(account.userId).trim() !== ""
        ? String(account.userId).trim()
        : user?.userId
          ? String(user.userId).trim()
          : "";
  const userName = user?.username ?? "You";
  const appRole = user?.role ?? null;
  const { projectId } = useParams();

  const { emitJoinConversation } = useChatSocket(userId);

  const loadConversations = useCallback(async () => {
    const token = getAccessToken();
    if (!token || !userId) return;
    dispatch(setConversationsLoading(true));
    dispatch(setChatError(null));
    try {
      let pillarId;
      if (appRole === ROLES.PILLAR_CHAIR) {
        try {
          const body = await pillarChairService.getMyPillar();
          pillarId = body?.data?.id ?? undefined;
        } catch {
          pillarId = undefined;
        }
      }
      const result = await fetchConversations({
        appRole,
        userId,
        account,
        projectId: projectId || undefined,
        pillarId,
      });
      dispatch(setConversations(result.data));
      if (result.meta) {
        dispatch(setSidebarMeta(result.meta));
      }
      return result.data;
    } catch (e) {
      dispatch(setChatError(e?.message ?? "Failed to load conversations"));
      return undefined;
    } finally {
      dispatch(setConversationsLoading(false));
    }
    }, [dispatch, userId, appRole, account, projectId]);

  useEffect(() => {
    if (userId) loadConversations();
  }, [userId, loadConversations]);

  const loadMessages = useCallback(
    async (conversationId) => {
      const token = getAccessToken();
      if (!token || !conversationId || !userId) return;
      dispatch(setMessagesLoading(true));
      try {
        const { data } = await fetchMessages(conversationId, userId);
        dispatch(
          setMessagesForConversation({ conversationId, messages: data }),
        );
      } catch (e) {
        dispatch(setChatError(e?.message ?? "Failed to load messages"));
      } finally {
        dispatch(setMessagesLoading(false));
      }
    },
    [dispatch, userId],
  );

  useEffect(() => {
    if (activeId && userId && getAccessToken()) {
      loadMessages(activeId);
    }
  }, [activeId, userId, loadMessages]);

  const selectConversation = useCallback(
    (id) => {
      dispatch(setActiveConversationId(id));
      // Ensure the socket is subscribed to this conversation's room so messages
      // sent via HTTP (axios) by other users arrive in real time without refresh.
      emitJoinConversation?.(id);
      // Mark as read only when the user explicitly selects a conversation,
      // never on automatic page-load selection.
      markConversationReadApi(id)
        .then(() => dispatch(markConversationRead(id)))
        .catch(() => {});
    },
    [dispatch, emitJoinConversation],
  );

  /**
   * Get (or create) the 1:1 conversation with `targetUserId` and put it in the store,
   * without making it the active one. Callers that fan out over several people need the
   * conversation before deciding which to focus.
   * @returns {Promise<object|null>} the conversation, or null when it could not be opened
   */
  const ensureDirectConversation = useCallback(
    async (targetUserId) => {
      const token = getAccessToken();
      if (!token || !targetUserId || !userId) return null;
      dispatch(setChatError(null));
      try {
        const { data } = await createOrGetDirectConversation(targetUserId);
        if (!data?.id) return null;
        dispatch(upsertConversation(data));
        return data;
      } catch (e) {
        // The axios message is only "Request failed with status code 403"; the reason
        // the server gave (role not allowed to start this chat, user not found) is the
        // part worth keeping.
        dispatch(
          setChatError(
            e?.response?.data?.message ?? e?.message ?? "Failed to open chat",
          ),
        );
        return null;
      }
    },
    [dispatch, userId],
  );

  const openDirectConversation = useCallback(
    async (targetUserId) => {
      const conversation = await ensureDirectConversation(targetUserId);
      if (conversation?.id) selectConversation(conversation.id);
      return Boolean(conversation);
    },
    [ensureDirectConversation, selectConversation],
  );

  /** Send to an explicit conversation — not necessarily the active one. */
  const sendMessageToConversation = useCallback(
    async (conversationId, body) => {
      const trimmed = typeof body === "string" ? body.trim() : "";
      const token = getAccessToken();
      if (!token || !trimmed || !conversationId || !userId) return false;
      const optimistic = {
        id: `opt-${Date.now()}-${conversationId}`,
        conversationId,
        senderId: userId,
        authorId: userId,
        senderName: userName,
        body: trimmed,
        createdAt: new Date().toISOString(),
        isOwn: true,
      };
      dispatch(addMessage(optimistic));
      try {
        await sendMessageApi({
          conversationId,
          body: trimmed,
          senderId: userId,
          senderName: userName,
        });
        return true;
      } catch (e) {
        dispatch(setChatError(e?.message ?? "Send failed"));
        return false;
      }
    },
    [userId, userName, dispatch],
  );

  const sendMessage = useCallback(
    async (body) => sendMessageToConversation(activeId, body),
    [activeId, sendMessageToConversation],
  );

  return {
    conversations,
    activeConversation,
    activeConversationId: activeId,
    messages,
    loadConversations,
    loadMessages,
    selectConversation,
    sendMessage,
    sendMessageToConversation,
    ensureDirectConversation,
    userId,
    userName,
    appRole,
    openDirectConversation,
  };
}
